import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { FilePlus, Pencil, Search, X, Trash2, ArrowLeft, Check } from 'lucide-react';
import { useEditDocument, EditabilityBanner, saveErrorMessage } from '../nativerecords/documentEdit';
import './NativeOrderNew.css';

// New Order (Native) — manual sales-order entry on the native platform.
// Replaces hand-typing odd SOs (samples, comps, replacements, one-offs) into
// NetSuite. Creates a native order (channel 'manual'); the NetSuite copy is
// produced by the reverse-sync worker when NATIVE_SO_AUTHORITY_MANUAL is on.
//
// This is the first CREATE screen on the native platform — the layout
// (preset cards → picker → line editor → review → confirmation) is the
// pattern for the PO and WO screens to follow.
//
// EDIT MODE: mounted at /native-orders/:id/edit the same screen loads that
// order, prefills itself, and saves with PATCH. A manual sales order posts
// nothing, so an amendment is a data change — but the server still decides
// what may change (nativeDocEdit.js) and the verdict rides in on
// `editability`: a partly-shipped order goes read-only except for quantities,
// shipping, and the memo, and an order that already has a NetSuite copy
// refuses outright.

// Subsidiary + warehouse choices (NS ids). The default warehouse follows the
// subsidiary — same mapping as NATIVE_RECEIVING_LOCATIONS (2:1, 3:32, 9:36).
const SUBSIDIARIES = [
  { id: '2', label: 'Rewined' },
  { id: '3', label: 'Candlefish — Charleston' },
  { id: '9', label: 'Candlefish — Boston' },
];
const WAREHOUSES = [
  { id: '1', label: 'Rewined Warehouse' },
  { id: '32', label: 'Candlefish CHS — Goer Dr' },
  { id: '36', label: 'BOS — Candlefish' },
  { id: '33', label: 'Candlefish (wholesale)' },
  { id: '10', label: 'CHS — Candlefish Store' },
  { id: '14', label: 'Candlefish Atlanta' },
];
const DEFAULT_WAREHOUSE_BY_SUB = { 2: '1', 3: '32', 9: '36' };

const ORDER_TYPES = [
  { key: 'standard', label: 'Standard', hint: 'Normal rates. A regular one-off order.' },
  { key: 'sample', label: 'Sample', hint: 'All rates $0, memo tagged SAMPLE, defaults to Pickup.' },
  { key: 'replacement', label: 'Replacement', hint: 'All rates $0, memo tagged REPLACEMENT.' },
];

const SHIP_METHODS = [
  { id: '', label: 'None / decide later' },
  { id: '16264', label: 'UPS Ground' },
  { id: '5567', label: 'Local Delivery' },
  { id: '16289', label: 'Pickup' },
];

const unitForSku = (sku) => {
  const s = String(sku || '').toUpperCase();
  if (/^(RW|CF)1/.test(s)) return 'single';
  if (/^(RW|CF)2/.test(s)) return 'case';
  return 'other';
};

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const newRef = () => `MAN-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

function useDebouncedSearch(url, minLen = 2) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    if (query.trim().length < minLen) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        setSearching(true);
        const { data } = await axios.get(url, { params: { q: query.trim() } });
        setResults(data.customers || data.items || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, url, minLen]);
  return { query, setQuery, results, setResults, searching };
}

export default function NativeOrderNew() {
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const [step, setStep] = useState('edit'); // edit | review | done
  const [orderType, setOrderType] = useState('standard');
  const [subsidiaryId, setSubsidiaryId] = useState('2');
  const [locationId, setLocationId] = useState('1');
  const [locationTouched, setLocationTouched] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [lines, setLines] = useState([]);
  const [shipMethod, setShipMethod] = useState('');
  const [shipMethodTouched, setShipMethodTouched] = useState(false);
  const [shippingTotal, setShippingTotal] = useState('');
  const [memo, setMemo] = useState('');
  const [replacementFor, setReplacementFor] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const externalRef = useRef(newRef());

  const customerSearch = useDebouncedSearch('/api/native-orders/customers');
  const itemSearch = useDebouncedSearch('/api/native-orders/items');

  // ── edit mode ──────────────────────────────────────────────────────────────
  const [editability, setEditability] = useState(null);
  const [orderNumber, setOrderNumber] = useState(null);
  const expectedUpdatedAt = useRef(undefined);
  const loaded = useEditDocument(isEdit ? `/api/native-orders/${encodeURIComponent(editId)}` : null, (d) => d.order);

  useEffect(() => {
    const order = loaded.doc;
    if (!order) return;
    expectedUpdatedAt.current = order.updated_at ?? null;
    externalRef.current = order.external_order_id;
    setEditability(loaded.editability);
    setOrderNumber(order.external_order_number || order.id);
    setOrderType((order.source_payload && order.source_payload.order_type) || 'standard');
    setCustomer({
      nsCustomerId: order.ns_customer_id,
      name: order.customer_name,
      email: order.customer_email,
      source: (order.source_payload && order.source_payload.customer_source) || 'accounting',
    });
    // Line ids ride along: the API reconciles by id, so dropping them would
    // delete and re-create every line.
    setLines((order.lines || []).map((l) => ({
      id: l.id,
      sku: l.sku,
      name: l.description || l.sku,
      nsItemId: l.ns_item_id,
      unitOfSale: l.unit_of_sale || unitForSku(l.sku),
      quantity: Number(l.quantity),
      rate: Number(l.rate),
      quantityFulfilled: Number(l.quantity_fulfilled || 0),
    })));
    setShipMethod(order.ship_method || '');
    setShipMethodTouched(true);
    setShippingTotal(order.shipping_total != null ? String(order.shipping_total) : '');
    setMemo(order.memo || '');
    const sub = order.subsidiary_id != null ? String(order.subsidiary_id) : '2';
    setSubsidiaryId(sub);
    setLocationId(order.location_ns_id != null ? String(order.location_ns_id) : (DEFAULT_WAREHOUSE_BY_SUB[sub] || '1'));
    setLocationTouched(true);
  }, [loaded.doc, loaded.editability]);

  const locked = isEdit && editability != null && !editability.editable;
  const restricted = isEdit && editability != null && editability.mode === 'restricted';

  // Make Copy (?copyFrom=<sales-order record id>): prefill customer + lines
  // from the mirrored record. Prices/quantities copy as-is — review first.
  const [searchParams] = useSearchParams();
  const [copyNote, setCopyNote] = useState(null);
  useEffect(() => {
    const copyFrom = searchParams.get('copyFrom');
    if (!copyFrom || isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/native-records/sales-orders/${encodeURIComponent(copyFrom)}`);
        if (cancelled) return;
        const rec = data.record || {};
        // Customer: re-find through the same picker endpoint so the object
        // shape matches a manual selection exactly.
        if (rec.customer_name) {
          try {
            const { data: cs } = await axios.get('/api/native-orders/customers', { params: { q: rec.customer_name } });
            const cands = cs.customers || [];
            const found = cands.find((c) => rec.customer_id != null && String(c.nsCustomerId) === String(rec.customer_id)) || cands[0];
            if (found && !cancelled) setCustomer(found);
          } catch { /* customer stays unset — operator picks */ }
        }
        const mapped = [];
        for (const l of data.lines || []) {
          if (!(Number(l.quantity) > 0) || l.item_id == null) continue;
          let sku = l.item_sku || null;
          let name = l.item_name || '';
          try {
            const { data: it } = await axios.get(`/api/native-items/${encodeURIComponent(l.item_id)}`);
            sku = it.item?.sku || sku;
            name = it.item?.name || name;
          } catch { /* fall back to the mirror line's own fields */ }
          if (!sku) continue;
          mapped.push({
            sku,
            name,
            nsItemId: String(l.item_id),
            unitOfSale: unitForSku(sku),
            quantity: Number(l.quantity),
            rate: Number(l.unit_price) || 0,
          });
        }
        if (cancelled) return;
        setLines(mapped);
        setMemo(`Copy of ${rec.order_number || copyFrom}`);
        setCopyNote(`Copied ${mapped.length} line${mapped.length === 1 ? '' : 's'} from ${rec.order_number || 'the source order'} — review customer, quantities, and rates before posting.`);
      } catch (err) {
        if (!cancelled) setCopyNote(`Could not copy from the source order: ${err.response?.data?.error || err.message}`);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const zeroRated = orderType !== 'standard';

  // Sample orders default to Pickup unless the operator already chose a method.
  useEffect(() => {
    if (orderType === 'sample' && !shipMethodTouched) setShipMethod('16289');
  }, [orderType, shipMethodTouched]);

  const effectiveRate = (line) => (zeroRated ? 0 : Number(line.rate) || 0);
  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + effectiveRate(l) * (Number(l.quantity) || 0), 0),
    [lines, zeroRated] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const shipping = Number(shippingTotal) || 0;
  const total = subtotal + shipping;

  const addItem = (item) => {
    setLines((prev) => [...prev, {
      sku: item.sku,
      name: item.name,
      nsItemId: item.nsItemId,
      unitOfSale: item.unitOfSale || unitForSku(item.sku),
      quantity: 1,
      rate: item.price != null ? item.price : 0,
    }]);
    itemSearch.setQuery('');
    itemSearch.setResults([]);
  };

  const updateLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const validate = () => {
    if (!customer) return 'Pick a customer first.';
    if (lines.length === 0) return 'Add at least one line item.';
    for (const l of lines) {
      if (!(Number(l.quantity) > 0)) return `Quantity for ${l.sku || l.name} must be greater than 0.`;
      if (Number(l.rate) < 0 || !Number.isFinite(Number(l.rate))) return `Rate for ${l.sku || l.name} must be 0 or more.`;
    }
    if (shipping < 0) return 'Shipping cost must be 0 or more.';
    return null;
  };

  const goReview = () => {
    const problem = validate();
    if (problem) { setError(problem); return; }
    setError(null);
    setStep('review');
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        externalRef: externalRef.current,
        orderType,
        customer: {
          nsCustomerId: customer.nsCustomerId,
          name: customer.name,
          email: customer.email,
          source: customer.source,
        },
        lines: lines.map((l) => ({
          id: l.id,                       // kept lines are matched by id
          sku: l.sku,
          nsItemId: l.nsItemId,
          description: l.name,
          quantity: Number(l.quantity),
          rate: Number(l.rate),
          unitOfSale: l.unitOfSale,
        })),
        shipMethod: shipMethod || null,
        shippingTotal: shipping,
        memo: memo.trim() || null,
        replacementFor: orderType === 'replacement' ? replacementFor.trim() || null : null,
        subsidiaryId,
        locationId,
      };
      const { data } = isEdit
        ? await axios.patch(`/api/native-orders/${encodeURIComponent(editId)}`, { ...body, expectedUpdatedAt: expectedUpdatedAt.current })
        : await axios.post('/api/native-orders', body);
      if (isEdit) {
        // Re-arm the compare-and-swap so a second save from this screen works.
        expectedUpdatedAt.current = data.order?.updated_at ?? null;
        setEditability(data.editability || editability);
      }
      setConfirmation(data);
      setStep('done');
    } catch (err) {
      setError(saveErrorMessage(err));
      setStep('edit');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    externalRef.current = newRef();
    setStep('edit');
    setOrderType('standard');
    setCustomer(null);
    setLines([]);
    setShipMethod('');
    setShipMethodTouched(false);
    setShippingTotal('');
    setMemo('');
    setReplacementFor('');
    setConfirmation(null);
    setError(null);
  };

  // ── confirmation ───────────────────────────────────────────────────────────
  if (step === 'done' && confirmation) {
    const o = confirmation.order;
    return (
      <div className="nat-page">
        <div className="nat-card nat-confirm">
          <div className="nat-confirm-icon"><Check size={28} /></div>
          <h2>{isEdit ? 'Order updated' : 'Order created'}</h2>
          <div className="nat-confirm-grid">
            <span>Native order id</span><code>{o.id}</code>
            <span>Reference</span><code>{o.external_order_number}</code>
            <span>Customer</span><strong>{o.customer_name || o.ns_customer_id}</strong>
            <span>Type</span><strong>{(o.source_payload && o.source_payload.order_type) || orderType}</strong>
            <span>Total</span><strong>{money(o.total)}</strong>
          </div>
          <p className="nat-note">{confirmation.netsuiteNote || confirmation.note}</p>
          {isEdit ? (
            <button className="nat-btn nat-btn-primary" onClick={() => setStep('edit')}>Back to the order</button>
          ) : (
            <div className="nat-actions">
              <a className="nat-btn nat-btn-ghost" href={`/native-orders/${o.id}/edit`}>Edit this order</a>
              <button className="nat-btn nat-btn-primary" onClick={reset}>Create another order</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── review ─────────────────────────────────────────────────────────────────
  if (step === 'review') {
    return (
      <div className="nat-page">
        <div className="nat-header">
          <button className="nat-btn nat-btn-ghost" onClick={() => setStep('edit')}><ArrowLeft size={16} /> Back to edit</button>
          <h1>{isEdit ? `Review changes to ${orderNumber || 'this order'}` : 'Review order'}</h1>
        </div>
        {error && <div className="nat-error">{error}</div>}
        <div className="nat-card">
          <div className="nat-review-row"><span>Type</span><strong>{ORDER_TYPES.find((t) => t.key === orderType)?.label}</strong></div>
          <div className="nat-review-row"><span>Customer</span><strong>{customer?.name}</strong> <em>{customer?.email}</em> <code>NS {customer?.nsCustomerId}</code></div>
          {orderType === 'replacement' && replacementFor && (
            <div className="nat-review-row"><span>Replacement for</span><strong>{replacementFor}</strong></div>
          )}
          <table className="nat-table">
            <thead><tr><th>SKU</th><th>Item</th><th>Unit</th><th className="r">Qty</th><th className="r">Rate</th><th className="r">Amount</th></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td><code>{l.sku}</code></td>
                  <td>{l.name}</td>
                  <td><span className={`nat-unit nat-unit-${l.unitOfSale}`}>{l.unitOfSale}</span></td>
                  <td className="r">{l.quantity}</td>
                  <td className="r">{zeroRated ? <span><s>{money(l.rate)}</s> {money(0)}</span> : money(l.rate)}</td>
                  <td className="r">{money(effectiveRate(l) * l.quantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="nat-totals">
            <div><span>Subtotal</span><strong>{money(subtotal)}</strong></div>
            <div><span>Shipping</span><strong>{money(shipping)}</strong></div>
            <div className="nat-total-line"><span>Total</span><strong>{money(total)}</strong></div>
          </div>
          <div className="nat-review-row"><span>Ship method</span><strong>{SHIP_METHODS.find((m) => m.id === shipMethod)?.label || 'None'}</strong></div>
          {memo && <div className="nat-review-row"><span>Memo</span><em>{memo}</em></div>}
          <div className="nat-actions">
            <button className="nat-btn nat-btn-ghost" onClick={() => setStep('edit')}>Back</button>
            <button className="nat-btn nat-btn-primary" disabled={submitting || locked} onClick={submit}>
              {submitting ? 'Saving…' : (isEdit ? 'Save changes' : 'Create order')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── edit ───────────────────────────────────────────────────────────────────
  return (
    <div className="nat-page">
      <div className="nat-header">
        {isEdit ? (
          <>
            <h1><Pencil size={22} /> Edit Order {orderNumber || ''}</h1>
            <p className="nat-sub">A manual sales order posts nothing, so changes save in place — no reversal, no re-entry.</p>
          </>
        ) : (
          <>
            <h1><FilePlus size={22} /> New Order (Native)</h1>
            <p className="nat-sub">Manual sales order on the native platform — samples, comps, replacements, one-offs. No NetSuite typing.</p>
          </>
        )}
      </div>
      {loaded.loading && <div className="nat-card"><p className="nat-sub" style={{ margin: 0 }}>Loading the order…</p></div>}
      {loaded.error && <div className="nat-error">{loaded.error}</div>}
      <EditabilityBanner editability={editability} prefix="nat" />
      {copyNote && <div className="nat-card"><p className="nat-sub" style={{ margin: 0 }}>{copyNote}</p></div>}
      {error && <div className="nat-error">{error}</div>}

      <div className="nat-card">
        <h3>Order type</h3>
        <div className="nat-presets">
          {ORDER_TYPES.map((t) => (
            <button
              key={t.key}
              className={`nat-preset ${orderType === t.key ? 'active' : ''}`}
              disabled={locked || restricted}
              onClick={() => setOrderType(t.key)}
            >
              <strong>{t.label}</strong>
              <span>{t.hint}</span>
            </button>
          ))}
        </div>
        {orderType === 'replacement' && (
          <input
            className="nat-input"
            placeholder="Replacing order # (optional — goes in the memo)"
            value={replacementFor}
            onChange={(e) => setReplacementFor(e.target.value)}
          />
        )}
      </div>

      <div className="nat-card">
        <h3>Subsidiary &amp; warehouse</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ flex: 1, minWidth: 220 }}>
            <span className="nat-sub">Subsidiary</span>
            <select
              className="nat-input"
              value={subsidiaryId}
              disabled={locked || restricted}
              onChange={(e) => {
                const sub = e.target.value;
                setSubsidiaryId(sub);
                if (!locationTouched) setLocationId(DEFAULT_WAREHOUSE_BY_SUB[sub] || '1');
              }}
            >
              {SUBSIDIARIES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
          <label style={{ flex: 1, minWidth: 220 }}>
            <span className="nat-sub">Warehouse (fulfills from)</span>
            <select
              className="nat-input"
              value={locationId}
              disabled={locked || restricted}
              onChange={(e) => { setLocationId(e.target.value); setLocationTouched(true); }}
            >
              {WAREHOUSES.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
            </select>
          </label>
        </div>
      </div>

      <div className="nat-card">
        <h3>Customer</h3>
        {customer ? (
          <div className="nat-chip">
            <div>
              <strong>{customer.name}</strong>
              {customer.email && <em>{customer.email}</em>}
              <code>NS {customer.nsCustomerId}</code>
              <span className={`nat-tag nat-tag-${customer.source}`}>{customer.source}</span>
            </div>
            {!locked && !restricted && (
              <button className="nat-icon-btn" onClick={() => setCustomer(null)} title="Change customer"><X size={16} /></button>
            )}
          </div>
        ) : (
          <div className="nat-search">
            <Search size={16} className="nat-search-icon" />
            <input
              className="nat-input nat-input-search"
              placeholder="Search customers by name or email…"
              value={customerSearch.query}
              onChange={(e) => customerSearch.setQuery(e.target.value)}
            />
            {(customerSearch.results.length > 0 || customerSearch.searching) && (
              <div className="nat-results">
                {customerSearch.searching && <div className="nat-result-muted">Searching…</div>}
                {customerSearch.results.map((c) => (
                  <button key={`${c.source}-${c.nsCustomerId}`} className="nat-result" onClick={() => { setCustomer(c); customerSearch.setQuery(''); customerSearch.setResults([]); }}>
                    <strong>{c.name}</strong>
                    {c.email && <em>{c.email}</em>}
                    <span className={`nat-tag nat-tag-${c.source}`}>{c.source}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="nat-card">
        <h3>Line items</h3>
        <div className="nat-search">
          <Search size={16} className="nat-search-icon" />
          <input
            className="nat-input nat-input-search"
            placeholder={restricted ? 'Lines cannot be added once part of the order has shipped' : 'Add item — search by SKU or name…'}
            value={itemSearch.query}
            disabled={locked || restricted}
            onChange={(e) => itemSearch.setQuery(e.target.value)}
          />
          {(itemSearch.results.length > 0 || itemSearch.searching) && (
            <div className="nat-results">
              {itemSearch.searching && <div className="nat-result-muted">Searching…</div>}
              {itemSearch.results.map((it) => (
                <button key={it.nsItemId} className="nat-result" onClick={() => addItem(it)}>
                  <code>{it.sku}</code>
                  <strong>{it.name}</strong>
                  <span className={`nat-unit nat-unit-${it.unitOfSale}`}>{it.unitOfSale}</span>
                  {it.price != null && <em>{money(it.price)}</em>}
                </button>
              ))}
            </div>
          )}
        </div>

        {lines.length > 0 && (
          <table className="nat-table">
            <thead><tr><th>SKU</th><th>Item</th><th>Unit</th><th className="r">Qty</th><th className="r">Rate{zeroRated ? ' (forced $0)' : ''}</th><th className="r">Amount</th><th /></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.id || i}>
                  <td><code>{l.sku}</code></td>
                  <td>
                    {l.name}
                    {l.quantityFulfilled > 0 && (
                      <div className="nat-hint">{l.quantityFulfilled} already shipped — this line cannot go below that.</div>
                    )}
                  </td>
                  <td><span className={`nat-unit nat-unit-${l.unitOfSale}`}>{l.unitOfSale}</span></td>
                  <td className="r"><input type="number" min={l.quantityFulfilled || 1} step="1" className="nat-input nat-input-num" value={l.quantity} disabled={locked} onChange={(e) => updateLine(i, { quantity: e.target.value })} /></td>
                  <td className="r">
                    <input type="number" min="0" step="0.01" className="nat-input nat-input-num" value={l.rate} disabled={zeroRated || locked || restricted} onChange={(e) => updateLine(i, { rate: e.target.value })} />
                  </td>
                  <td className="r">{money(effectiveRate(l) * (Number(l.quantity) || 0))}</td>
                  <td>
                    {!locked && !restricted && (
                      <button className="nat-icon-btn" onClick={() => removeLine(i)} title="Remove line"><Trash2 size={15} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {zeroRated && lines.length > 0 && (
          <p className="nat-hint">{orderType === 'sample' ? 'Sample' : 'Replacement'} orders bill at $0 — original rates are kept on the order for reference.</p>
        )}
      </div>

      <div className="nat-card">
        <h3>Shipping and notes</h3>
        <div className="nat-form-grid">
          <label>
            Ship method
            <select className="nat-input" value={shipMethod} disabled={locked} onChange={(e) => { setShipMethod(e.target.value); setShipMethodTouched(true); }}>
              {SHIP_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </label>
          <label>
            Shipping cost
            <input type="number" min="0" step="0.01" className="nat-input" placeholder="0.00" value={shippingTotal} disabled={locked} onChange={(e) => setShippingTotal(e.target.value)} />
          </label>
        </div>
        <label className="nat-label-block">
          Memo
          <textarea className="nat-input" rows={2} placeholder="Optional note — appears on the order and the NetSuite copy" value={memo} disabled={locked} onChange={(e) => setMemo(e.target.value)} />
        </label>
      </div>

      <div className="nat-footer">
        <div className="nat-footer-totals">
          <span>Subtotal {money(subtotal)}</span>
          <span>Shipping {money(shipping)}</span>
          <strong>Total {money(total)}</strong>
        </div>
        <button className="nat-btn nat-btn-primary" onClick={goReview} disabled={!customer || lines.length === 0 || locked}>
          {isEdit ? 'Review changes' : 'Review order'}
        </button>
      </div>
    </div>
  );
}
